/* eslint-disable @trello/disallow-filenames */
import type { ActionDisplayType } from 'app/src/components/ActionEntities';
import type { OrganizationLimits, NonPublicFields } from './responses';
import type { TeamTypes } from '@trello/organizations';
import type { EnterpriseLicenseType } from '@trello/enterprise';
import type { CardRole } from '@trello/card-roles';
import type { NotificationEmailFrequency } from '@trello/notifications';
import type { NotificationType } from 'app/src/components/NotificationsMenu/NotificationsMenu.types';
import type { AvatarSources } from 'app/src/components/MemberAvatar/MemberAvatar.types';
import type { PermissionLevel } from '@trello/boards';

export interface AppCreatorModel {
  name?: string;
  id?: string;
  idPlugin?: string;
  iconClass?: string;
  urlSuffix?: string;
}

export interface ButlerRuleDataModel {
  id: string;
  text: string;
  url: string;
}

export interface ActionDataModel {
  actionType?: string;
  board?: BoardModel;
  card?: CardModel;
  cardSource?: object; // TODO create this type
  checklist?: ChecklistModel;
  checkItem?: ChecklistItemModel;
  idExport?: string;
  list?: ListModel;
  listAfter?: ListModel;
  listBefore?: ListModel;
  name?: string;
  plugin?: object; // TODO create this type
  rule?: ButlerRuleDataModel;
  organization?: TeamModel;
  text?: string;
  signature?: string;
}

export interface AttachmentModel {
  date: Date;
  id: string;
  isUpload: boolean;
  mimeType?: string;
  name: string;
  previews?: AttachmentPreviewModel[];
  url: string;
}

export interface AttachmentPreviewModel {
  _id: string;
  bytes: number;
  height: number;
  scaled: boolean;
  url: string;
  width: number;
}

export interface BadgesModel {
  attachments: number;
  attachmentsByType: {
    trello: {
      board: number;
      card: number;
    };
  };
  checkItems: number;
  checkItemsChecked: number;
  comments: number;
  description: boolean;
  due?: Date | null;
  dueComplete: boolean;
  start?: Date | null;
  subscribed: boolean;
  viewingMemberVoted: boolean;
  votes: number;
  checkItemsEarliestDue?: Date | null;
}

export interface BoardBackgroundModel {
  backgroundBottomColor: string | null;
  backgroundBrightness: 'dark' | 'light' | 'unknown';
  backgroundTopColor: string | null;
  backgroundTile: boolean;
  backgroundImage?: string | null;
  backgroundImageScaled?: BackgroundImagePreviewModel[];
}

export type BoardCreationMethod = 'demo' | 'automatic' | 'assisted';

export interface BoardModel {
  background?: BoardBackgroundModel;
  closed?: boolean;
  creationMethod?: BoardCreationMethod | null;
  dateLastActivity?: Date | null;
  dateLastView?: Date;
  id: string;
  idOrganization?: string | null;
  starred?: boolean;
  enterpriseOwned?: boolean;
  memberships?: MembershipModel[];
  name?: string;
  prefs?: BoardPreferencesModel;
  premiumFeatures?: string[];
  shortLink?: string;
  url?: string;
}

export interface BoardPreferencesModel {
  cardCovers: boolean;
  comments: string;
  permissionLevel: PermissionLevel;
  isTemplate: boolean;
}
export interface BoardStarModel {
  deleted?: boolean;
  id: string;
  idBoard: string;
  pos: number;
}

export interface BoardsMenuSelectedItemModel {
  id: string;
  category: string | null;
  url: string | undefined;
}

export interface ReopenBoardPopoverModel {
  idBoard: string;
  newBillableGuests: NewBillableGuestsModel[];
  availableLicenseCount: number | null;
  adminNames: string[];
}

export interface NewBillableGuestsModel {
  confirmed: boolean;
  fullName?: string | null;
  id: string;
  initials: string;
  username: string;
}

export interface CardModel {
  badges?: BadgesModel;
  closed?: boolean;
  cover?: CoverPhotoModel | null;
  desc?: string;
  due?: Date;
  dueComplete?: boolean;
  id: string;
  attachments?: AttachmentModel[];
  idBoard?: string;
  idChecklists?: string[];
  idLabels?: string[];
  idList?: string;
  idMembers?: string[];
  idShort?: number;
  isTemplate?: boolean;
  list?: ListModel;
  name?: string;
  pos?: number;
  url?: string;
  shortLink?: string;
  start?: Date;
  stickers?: StickerModel[];
  cardRole?: CardRole | null;
}

export interface ChecklistModel {
  id: string;
  idBoard: string;
  idCard: string;
  checkItems: ChecklistItemModel[];
  name: string;
  pos: number;
}

export interface ChecklistItemModel {
  id: string;
  name: string;
  nameData: object; // TODO: figure out what this is
  pos: number;
}

export type CoverColor =
  | 'blue'
  | 'green'
  | 'orange'
  | 'purple'
  | 'red'
  | 'yellow'
  | 'pink'
  | 'sky'
  | 'lime'
  | 'black';

export interface CoverPhotoModel {
  url?: string;
  edgeColor?: string | null;
  previews?: ImagePreviewModel[];
  sharedSourceUrl?: string;
  color?: CoverColor | null;
  size?: 'normal' | 'full';
  brightness?: 'light' | 'dark';
}

export interface ImagePreviewModel {
  scaled: boolean;
  height: number;
  url: string;
  width: number;
}

export type BackgroundImagePreviewModel = Pick<
  ImagePreviewModel,
  'url' | 'height' | 'width'
>;

export interface LabelModel {
  color?: LabelColor | null;
  id: string;
  idBoard: string;
  name: string;
}

export type LabelColor =
  | 'black'
  | 'black_dark'
  | 'black_light'
  | 'blue'
  | 'blue_dark'
  | 'blue_light'
  | 'green'
  | 'green_dark'
  | 'green_light'
  | 'lime'
  | 'lime_dark'
  | 'lime_light'
  | 'orange'
  | 'orange_dark'
  | 'orange_light'
  | 'pink'
  | 'pink_dark'
  | 'pink_light'
  | 'purple'
  | 'purple_dark'
  | 'purple_light'
  | 'red'
  | 'red_dark'
  | 'red_light'
  | 'sky'
  | 'sky_dark'
  | 'sky_light'
  | 'yellow'
  | 'yellow_dark'
  | 'yellow_light';

export interface ListModel {
  closed: boolean;
  id: string;
  idBoard: string;
  name: string;
  pos: number;
  subscribed?: boolean;
}

export interface LoginModel {
  email: string;
  id: string;
  primary: boolean;
}

export interface EnterpriseModel {
  displayName: string;
  id: string;
  name: string;
  logoHash?: string;
  logoUrl?: string;
  organizationPrefs?: TeamPreferencesModel;
  isAtlassianOrg?: boolean;
  atlOrgId?: string;
}

export type PrivacyModel = { [key in NonPublicFields]?: string | null };

export interface MemberMessagesDismissedModel {
  _id: string;
  count: number;
  lastDismissed: string;
  name: string;
}

export type MemberType = 'ghost' | 'normal';

export interface MemberEnterpriseLicense {
  idEnterprise: string;
  type: EnterpriseLicenseType;
}
export interface MemberModel {
  activityBlocked?: boolean;
  avatars?: { [size: string]: string } | null;
  avatarSource?: AvatarSources;
  credentialsRemovedCount?: string | null;
  confirmed?: boolean;
  enterpriseLicenses?: MemberEnterpriseLicense[];
  enterprises?: EnterpriseModel[];
  id: string;
  idBoards?: string[];
  idEnterprise?: string | null;
  idEnterprisesAdmin?: string[];
  idOrganizations?: string[];
  idPremOrgsAdmin: string[];
  initials?: string;
  logins?: LoginModel[];
  marketingOptIn?: MarketingOptInModel;
  memberType?: MemberType;
  messagesDismissed?: MemberMessagesDismissedModel[] | null;
  fullName?: string;
  nonPublic?: PrivacyModel;
  oneTimeMessagesDismissed?: string[] | null;
  prefs?: MemberPreferencesModel;
  products?: number[];
  savedSearches?: SavedSearchModel[];
  username?: string;
}

export interface MemberPreferencesModel {
  colorBlind: boolean;
  locale: string;
  minutesBeforeDeadlineToNotify: number;
  minutesBetweenSummaries: NotificationEmailFrequency;
  sendSummaries?: boolean;
  timezoneInfo: {
    dateNext: string;
    offsetCurrent: number;
    offsetNext: number;
    timezoneCurrent: string;
    timezoneNext: string;
  };
  twoFactor?: {
    enabled: boolean;
    needsNewBackups: boolean;
  } | null;
}

export type MarketingOptInModel =
  | object
  | {
      date: Date;
      optedIn: boolean;
    };

export type AccessLevel =
  | 'admin'
  | 'enterprise'
  | 'normal'
  | 'public'
  | 'private'
  | 'org'
  | 'observer'
  | 'virtual'
  | 'ghost'
  | 'deactivated'
  | 'unconfirmed'
  | 'none';

export type MembershipType = 'admin' | 'normal' | 'observer';

export interface MembershipModel {
  id: string;
  deactivated: boolean;
  idMember: string;
  memberType: MembershipType;
  unconfirmed: boolean;
}

export interface NotificationModel {
  appCreator?: AppCreatorModel | null;
  data?: ActionDataModel;
  date?: Date;
  display: ActionDisplayType;
  id: string;
  idAction?: string;
  idMemberCreator?: string;
  memberCreator?: MemberModel;
  type?: NotificationType;
  unread?: boolean;
  dateRead?: string | Date | null;
  isReactable?: boolean;
}

export interface NotificationGroupModel {
  id: string;
  idGroup: string;
  notifications: NotificationModel[];
  card?: CardModel;
  board?: BoardModel;
}

export interface NotificationsCountModel {
  [typeAndId: string]: number;
}
export interface SavedSearchModel {
  id: string;
  name: string;
  pos: number;
  query: string;
}

export interface SearchResultsModel {
  cardsPage: number;
  hasMoreCards: boolean;
  noMatchesFound: boolean;
  idBoards: string[];
  idCards: string[];
  idMembers: string[];
  idTeams: string[];
  totalCards: string[];
}

export enum SearchSuggestionType {
  IsArchived = 'is:archived',
  IsOpen = 'is:open',
  IsStarred = 'is:starred',
  HasAttachments = 'has:attachments',
  HasCover = 'has:cover',
  HasDescription = 'has:description',
  HasStickers = 'has:stickers',
  HasMembers = 'has:members',
  At = '@',
  Me = '@me',
  Member = 'member:',
  Label = 'label:',
  Hash = '#',
  Board = 'board:',
  List = 'list:',
  Name = 'name:',
  Comment = 'comment:',
  Checklist = 'checklist:',
  Description = 'description:',
  DueDay = 'due:day',
  DueWeek = 'due:week',
  DueMonth = 'due:month',
  Overdue = 'due:overdue',
  Incomplete = 'due:incomplete',
  Complete = 'due:complete',
  CreatedDay = 'created:day',
  CreatedWeek = 'created:week',
  CreatedMonth = 'created:month',
  EditedDay = 'edited:day',
  EditedWeek = 'edited:week',
  EditedMonth = 'edited:month',
  SortCreated = 'sort:created',
  SortEdited = 'sort:edited',
  SortDue = 'sort:due',
}

export interface SearchSuggestionsResultsModel {
  keywords: SearchSuggestionType[];
  idMembers: string[];
  idBoards: string[];
}

export interface StickerModel {
  id: string;
  image: string;
  imageUrl: string;
  imageScaled: string | null;
  left: number;
  top: number;
  rotate: number;
  zIndex: number;
}

export interface TeamModel {
  id: string;
  idEnterprise?: string | null;
  desc?: string;
  descData?: object;
  displayName: string;
  logos?: { [size: string]: string } | null;
  limits?: OrganizationLimits;
  memberships?: MembershipModel[];
  name: string;
  prefs?: TeamPreferencesModel;
  premiumFeatures?: string[];
  products?: number[];
  standardVariation?: string | null;
  teamType?: TeamTypes;
  text?: string;
  type?: string;
  url?: string;
  website?: string;
}

export interface TeamPreferencesModel {
  permissionLevel?: string | null;
  boardInviteRestrict?: string | null;
  orgInviteRestrict?: string[] | null;
  associatedDomain?: string | null;
  boardVisibilityRestrict?: Partial<
    Record<PermissionLevel, AccessLevel>
  > | null;
  boardDeleteRestrict?: Partial<Record<PermissionLevel, AccessLevel>> | null;
}
